import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { ReactComponent as WandIcon } from '../../assets/svgs/assistino-wand.svg'
import orbImage from '../../assets/images/assistino-orb.png'

// Animation for popup
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

const teaserIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(24px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`

const teaserInRtl = keyframes`
  from {
    opacity: 0;
    transform: translateX(-24px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`

const badgePop = keyframes`
  0% {
    opacity: 0;
    transform: translateY(6px) scale(0.6);
  }
  60% {
    opacity: 1;
    transform: translateY(0) scale(1.12);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const shimmer = keyframes`
  0% {
    transform: translateX(-120%);
  }
  60%, 100% {
    transform: translateX(220%);
  }
`

export const AssistinoButtonContainer = styled.div(({ theme, locale }) => ({
  position: 'fixed',
  bottom: '0px',
  right: locale === 'ar' ? 'auto' : '20px',
  left: locale === 'ar' ? '20px' : 'auto',
  zIndex: 1000,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  transform: 'scale(1)',
  borderRadius: '50%',
  overflow: 'visible',
  width: '85px',
  height: '85px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: `url(${orbImage})`,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: 'transparent',
  '&:hover': {
    transform: 'scale(1.1)',
    '& .assistino-popup': {
      opacity: 1,
      visibility: 'visible',
      transform: 'translateY(0)',
    },
    '& svg': {
      transform:
        locale === 'ar' ? 'scaleX(-1) rotate(-10deg)' : 'rotate(10deg)',
    },
  },
}))

export const AssistinoIcon = styled(WandIcon)(({ theme, locale }) => ({
  width: '17px',
  height: '28px',
  color: theme.brandColorOrange,
  flexShrink: 0,
  transition: 'transform 0.3s ease',
  transform: locale === 'ar' ? 'scaleX(-1)' : 'none',
  ':dir(rtl)': {
    transform: 'scaleX(-1)',
  },
}))

export const AssistinoPopup = styled.div(({ theme, locale }) => ({
  position: 'absolute',
  bottom: '84px',
  right: locale === 'ar' ? 'auto' : '10px',
  left: locale === 'ar' ? '10px' : 'auto',
  backgroundColor: theme.brandColorOrange,
  color: 'white',
  padding: '12px 16px',
  borderRadius: '8px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'normal',
  width: 'max-content',
  maxWidth: '220px',
  textAlign: locale === 'ar' ? 'right' : 'left',
  opacity: 0,
  visibility: 'hidden',
  transform: 'translateY(10px)',
  transition: 'all 0.3s ease 0.1s',
  animation: `${fadeIn} 0.3s ease-out`,
  zIndex: 1001,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-6px',
    right: locale === 'ar' ? 'auto' : '20px',
    left: locale === 'ar' ? '20px' : 'auto',
    width: '12px',
    height: '12px',
    backgroundColor: theme.brandColorOrange,
    transform: 'rotate(45deg)',
  },
  ':dir(rtl)': {
    right: 'auto',
    left: '0',
    '&::after': {
      right: 'auto',
      left: '20px',
    },
  },
}))

const pulse = (color) => keyframes`
  0% {
    box-shadow: 0 0 0 0 ${color}66;
  }
  70% {
    box-shadow: 0 0 0 10px ${color}00;
  }
  100% {
    box-shadow: 0 0 0 0 ${color}00;
  }
`

export const AssistinoButtonPulse = styled.div(({ theme }) => ({
  position: 'absolute',
  top: '-5px',
  left: '-5px',
  right: '-5px',
  bottom: '-5px',
  borderRadius: '50%',
  animation: `${pulse(theme.brandColorOrange)} 2s infinite`,
  pointerEvents: 'none',
  zIndex: -1,
}))

export const AssistinoButtonWrapper = styled.div(({ theme, locale }) => ({
  position: 'relative',
  display: 'inline-block',
}))

export const AssistinoPopupTitle = styled.span({
  display: 'block',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: 1.3,
})

export const AssistinoPopupHint = styled.span({
  display: 'block',
  marginTop: '4px',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1.4,
  opacity: 0.9,
})

/**
 * Small "free" pill pinned to the orb so the offer stays visible
 * even after the teaser card has been dismissed.
 */
export const AssistinoTrialBadge = styled.span(({ theme, locale }) => ({
  position: 'absolute',
  top: '6px',
  right: locale === 'ar' ? 'auto' : '-2px',
  left: locale === 'ar' ? '-2px' : 'auto',
  padding: '3px 8px',
  borderRadius: '999px',
  background: `linear-gradient(135deg, ${theme.brandColorLightOrange}, ${theme.brandColorOrange})`,
  color: theme.white,
  fontSize: '9px',
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  boxShadow: `0 4px 10px ${theme.brandColorOrange}59`,
  border: `1.5px solid ${theme.white}`,
  pointerEvents: 'none',
  overflow: 'hidden',
  animation: `${badgePop} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '40%',
    background:
      'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent)',
    animation: `${shimmer} 2.6s ease-in-out infinite`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '&::after': { animation: 'none', display: 'none' },
  },
}))

export const AssistinoTrialTeaser = styled.div(({ theme, locale }) => {
  const isRtl = locale === 'ar'

  return {
    position: 'fixed',
    bottom: '28px',
    right: isRtl ? 'auto' : '104px',
    left: isRtl ? '104px' : 'auto',
    zIndex: 1000,
    width: '274px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '14px 16px',
    borderRadius: '16px',
    backgroundColor: theme.white,
    border: `1px solid ${theme.brightGray}`,
    boxShadow: '0 16px 36px rgba(4, 56, 80, 0.18)',
    cursor: 'pointer',
    textAlign: isRtl ? 'right' : 'left',
    direction: isRtl ? 'rtl' : 'ltr',
    // no fill-mode: the resting state already matches the last keyframe,
    // which keeps the hover transform working once the entrance is done
    animation: `${isRtl ? teaserInRtl : teaserIn} 0.45s cubic-bezier(0.16, 1, 0.3, 1)`,
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    // gradient accent along the outer edge, opposite the pointer
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '14px',
      bottom: '14px',
      right: isRtl ? '0' : 'auto',
      left: isRtl ? 'auto' : '0',
      width: '3px',
      borderRadius: '3px',
      background: `linear-gradient(180deg, ${theme.brandColorLightOrange}, ${theme.brandColorOrange})`,
    },
    // pointer aimed at the orb
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '14px',
      right: isRtl ? 'auto' : '-6px',
      left: isRtl ? '-6px' : 'auto',
      width: '12px',
      height: '12px',
      backgroundColor: theme.white,
      borderTop: `1px solid ${theme.brightGray}`,
      borderRight: `1px solid ${theme.brightGray}`,
      transform: isRtl ? 'rotate(-135deg)' : 'rotate(45deg)',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 20px 44px rgba(4, 56, 80, 0.24)',
    },
    '@media (max-width: 767px)': {
      right: '16px',
      left: '16px',
      width: 'auto',
      bottom: '96px',
      '&::after': {
        display: 'none',
      },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  }
})

export const TeaserIconCircle = styled.span(({ theme }) => ({
  flexShrink: 0,
  width: '34px',
  height: '34px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.brandColorLightOrange}, ${theme.brandColorOrange})`,
  boxShadow: `0 6px 14px ${theme.brandColorOrange}40`,
}))

export const TeaserWandIcon = styled(WandIcon)(({ theme }) => ({
  width: '15px',
  height: '22px',
  color: theme.white,
}))

export const TeaserBody = styled.div({
  flex: 1,
  minWidth: 0,
})

export const TeaserTitle = styled.p(({ theme }) => ({
  margin: 0,
  fontSize: '13px',
  fontWeight: 700,
  lineHeight: 1.35,
  color: theme.brandColorOrange,
}))

export const TeaserText = styled.p(({ theme }) => ({
  margin: '3px 0 0',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1.5,
  color: theme.gray600,
}))

export const TeaserCta = styled.span(({ theme, locale }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '8px',
  fontSize: '12px',
  fontWeight: 700,
  color: theme.brandColorLightOrange,
  '&::after': {
    content: locale === 'ar' ? '"←"' : '"→"',
    transition: 'transform 0.2s ease',
  },
}))

export const TeaserClose = styled.button(({ theme, locale }) => ({
  position: 'absolute',
  top: '6px',
  right: locale === 'ar' ? 'auto' : '8px',
  left: locale === 'ar' ? '8px' : 'auto',
  width: '20px',
  height: '20px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  borderRadius: '50%',
  background: 'transparent',
  color: theme.gray400,
  fontSize: '14px',
  lineHeight: 1,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.brightGray,
    color: theme.gray700,
  },
}))
